import { FIELD_NAMES, GENDER_LABELS, ACTION_VERBS, IGNORED_FIELDS, IMPORTANT_FIELDS } from '../utils/change-formatter'
import { detectChangeType, extractObjectName, formatSimpleValue } from '../utils/change-detector'

export default {
  methods: {
    /**
     * Formats field names for display
     */
    formatFieldName (fieldName) {
      return FIELD_NAMES[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    /**
     * Formats field values based on field type
     */
    formatFieldValue (fieldName, value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      // Status formatting
      if (fieldName === 'status') {
        return this.$options.filters.formatStatus ? this.$options.filters.formatStatus(value) : value
      }

      // Campaign type formatting
      if (fieldName === 'campaign_type' || fieldName === 'type') {
        return this.$options.filters.formatCampaignType ? this.$options.filters.formatCampaignType(value) : value
      }

      // Creative format
      if (fieldName === 'creative_format') {
        return this.$options.filters.formatCreativeFormat ? this.$options.filters.formatCreativeFormat(value) : value
      }

      // Objective
      if (fieldName === 'objective') {
        return this.$options.filters.formatObjective ? this.$options.filters.formatObjective(value) : value
      }

      // Monetary values
      if (this.isMonetaryField(fieldName) && typeof value === 'number') {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(value)
      }

      // Date values
      if (fieldName.includes('date') && value && typeof value === 'string') {
        return this.$options.filters.formatDate ? this.$options.filters.formatDate(value, 'DD/MM/YYYY') : value
      }

      // Boolean values
      if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não'
      }

      // Object values
      if (typeof value === 'object') {
        return JSON.stringify(value)
      }

      return String(value)
    },

    /**
     * Checks if field is monetary
     */
    isMonetaryField (fieldName) {
      return ['budget', 'bid', 'amount'].some(field => fieldName.includes(field))
    },

    /**
     * Formats change action verb
     */
    formatChangeAction (change) {
      let verb = 'Modificou'

      for (const [action, label] of Object.entries(ACTION_VERBS)) {
        if (change.type.includes(action)) {
          verb = label
          break
        }
      }

      return `${verb} ${this.$options.filters.formatEntityNames(change.loggable_type)}`
    },

    /**
     * Formats age for display
     */
    formatAge (age) {
      if (age === '65+') return '65+ anos'
      if (age === 'UNKNOWN') return 'Não especificado'
      return `${age} anos`
    },

    /**
     * Formats gender for display
     */
    formatGender (gender) {
      return GENDER_LABELS[gender] || gender
    },

    /**
     * Gets entity changes for display
     */
    getEntityChanges () {
      if (!this.changeLog || !this.changeLog.type.includes('UPDATE')) {
        return []
      }

      const oldEntity = this.changeLog.old_entity
      const newEntity = this.changeLog.new_entity || this.changeLog.request

      if (!oldEntity || !newEntity) {
        return []
      }

      const changes = []
      const allKeys = new Set([...Object.keys(oldEntity), ...Object.keys(newEntity)])

      allKeys.forEach(key => {
        const oldValue = oldEntity[key]
        const newValue = newEntity[key]

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          const formattedChange = this.formatEntityChange(key, oldValue, newValue)
          if (formattedChange) {
            changes.push(formattedChange)
          }
        }
      })

      return changes.slice(0, 6)
    },

    /**
     * Formats entity change for display
     */
    formatEntityChange (fieldName, oldValue, newValue) {
      if (IGNORED_FIELDS.includes(fieldName)) {
        return null
      }

      const changeType = detectChangeType(fieldName, oldValue, newValue)

      return {
        field: fieldName,
        oldValue,
        newValue,
        changeType,
        description: this.generateChangeDescription(fieldName, oldValue, newValue, changeType)
      }
    },

    /**
     * Generates change description based on change type
     */
    generateChangeDescription (fieldName, oldValue, newValue, changeType) {
      const fieldDisplayName = this.formatFieldName(fieldName)

      switch (changeType) {
        case 'demographics':
          return this.formatDemographicsChange(oldValue, newValue)

        case 'object_modification':
          return this.formatObjectModification(fieldDisplayName, oldValue, newValue)

        case 'status_change': {
          const oldStatus = this.formatFieldValue('status', oldValue)
          const newStatus = this.formatFieldValue('status', newValue)
          return `<strong>Status:</strong> ${oldStatus} → ${newStatus}`
        }

        case 'monetary': {
          const oldMoney = this.formatFieldValue(fieldName, oldValue)
          const newMoney = this.formatFieldValue(fieldName, newValue)
          return `<strong>${fieldDisplayName}:</strong> ${oldMoney} → ${newMoney}`
        }

        case 'date': {
          const oldDate = this.formatFieldValue(fieldName, oldValue)
          const newDate = this.formatFieldValue(fieldName, newValue)
          return `<strong>${fieldDisplayName}:</strong> ${oldDate} → ${newDate}`
        }

        case 'boolean': {
          const oldBool = this.formatFieldValue(fieldName, oldValue)
          const newBool = this.formatFieldValue(fieldName, newValue)
          return `<strong>${fieldDisplayName}:</strong> ${oldBool} → ${newBool}`
        }

        case 'array':
          return this.formatArrayChange(fieldDisplayName, oldValue, newValue)

        default: {
          if (typeof oldValue === 'object' || typeof newValue === 'object') {
            return this.formatObjectModification(fieldDisplayName, oldValue, newValue)
          }
          const oldSimple = formatSimpleValue(oldValue)
          const newSimple = formatSimpleValue(newValue)
          return `<strong>${fieldDisplayName}:</strong> ${oldSimple} → ${newSimple}`
        }
      }
    },

    /**
     * Formats demographics changes
     */
    formatDemographicsChange (oldValue, newValue) {
      try {
        const oldDemo = typeof oldValue === 'string' ? JSON.parse(oldValue) : oldValue
        const newDemo = typeof newValue === 'string' ? JSON.parse(newValue) : newValue
        const changes = []

        // Age changes
        if (JSON.stringify(oldDemo.age) !== JSON.stringify(newDemo.age)) {
          const addedAges = newDemo.age ? newDemo.age.filter(age => !oldDemo.age?.includes(age)) : []
          const removedAges = oldDemo.age ? oldDemo.age.filter(age => !newDemo.age?.includes(age)) : []

          if (addedAges.length > 0) {
            const formattedAges = addedAges.map(age => this.formatAge(age)).join(', ')
            changes.push(`+Idade: ${formattedAges}`)
          }
          if (removedAges.length > 0) {
            const formattedAges = removedAges.map(age => this.formatAge(age)).join(', ')
            changes.push(`-Idade: ${formattedAges}`)
          }
        }

        // Gender changes
        if (JSON.stringify(oldDemo.gender) !== JSON.stringify(newDemo.gender)) {
          const addedGenders = newDemo.gender ? newDemo.gender.filter(gender => !oldDemo.gender?.includes(gender)) : []
          const removedGenders = oldDemo.gender ? oldDemo.gender.filter(gender => !newDemo.gender?.includes(gender)) : []

          if (addedGenders.length > 0) {
            const formattedGenders = addedGenders.map(g => this.formatGender(g)).join(', ')
            changes.push(`+Gênero: ${formattedGenders}`)
          }
          if (removedGenders.length > 0) {
            const formattedGenders = removedGenders.map(g => this.formatGender(g)).join(', ')
            changes.push(`-Gênero: ${formattedGenders}`)
          }
        }

        return changes.length > 0 ? `<strong>Demografia:</strong> ${changes.join('; ')}` : '<strong>Demografia</strong> modificada'
      } catch (e) {
        return '<strong>Demografia</strong> modificada'
      }
    },

    /**
     * Formats object modifications
     */
    formatObjectModification (fieldDisplayName, oldValue, newValue) {
      // Campaign package specific handling
      if (fieldDisplayName === 'Campaign Package' || fieldDisplayName === 'Grupo de Campanha') {
        return this.formatCampaignPackageChange(oldValue, newValue)
      }

      // Generic object handling
      if (oldValue && newValue === null) {
        const name = extractObjectName(oldValue)
        return `<strong>Removeu ${fieldDisplayName.toLowerCase()}:</strong> "${name}"`
      }

      if (oldValue === null && newValue) {
        const name = extractObjectName(newValue)
        return `<strong>Adicionou ${fieldDisplayName.toLowerCase()}:</strong> "${name}"`
      }

      if (oldValue && newValue) {
        const oldName = extractObjectName(oldValue)
        const newName = extractObjectName(newValue)

        if (oldName !== newName) {
          return `<strong>${fieldDisplayName}:</strong> "${oldName}" → "${newName}"`
        }

        const changes = this.getObjectChanges(oldValue, newValue)
        if (changes.length > 0) {
          return `<strong>${fieldDisplayName}:</strong> ${changes.slice(0, 2).join(', ')}`
        }
      }

      return `<strong>Modificou ${fieldDisplayName.toLowerCase()}</strong>`
    },

    /**
     * Formats campaign package changes
     */
    formatCampaignPackageChange (oldValue, newValue) {
      if (oldValue && newValue === null) {
        const campaignName = oldValue.campaign?.name || oldValue.name
        return `<strong>Removeu vinculação</strong> com "${campaignName || 'Grupo de Campanha'}"`
      }

      if (oldValue === null && newValue) {
        const campaignName = newValue.campaign?.name || newValue.name
        return `<strong>Adicionou vinculação</strong> com "${campaignName || 'Grupo de Campanha'}"`
      }

      if (oldValue && newValue) {
        const oldName = oldValue.campaign?.name || oldValue.name || 'Item'
        const newName = newValue.campaign?.name || newValue.name || 'Item'

        if (oldName !== newName) {
          return `<strong>Alterou vinculação:</strong> "${oldName}" → "${newName}"`
        }

        return '<strong>Atualizou dados</strong> do grupo de campanha'
      }

      return '<strong>Modificou grupo de campanha</strong>'
    },

    /**
     * Formats array changes
     */
    formatArrayChange (fieldName, oldArray, newArray) {
      const oldArr = Array.isArray(oldArray) ? oldArray : []
      const newArr = Array.isArray(newArray) ? newArray : []

      const added = newArr.filter(item => !oldArr.includes(item))
      const removed = oldArr.filter(item => !newArr.includes(item))

      const changes = []
      if (added.length > 0) {
        changes.push(`+${added.join(', ')}`)
      }
      if (removed.length > 0) {
        changes.push(`-${removed.join(', ')}`)
      }

      return `<strong>${fieldName}:</strong> ${changes.join('; ')}`
    },

    /**
     * Gets object changes for comparison
     */
    getObjectChanges (oldObj, newObj) {
      if (!oldObj || !newObj) return []

      const changes = []

      IMPORTANT_FIELDS.forEach(field => {
        if (oldObj[field] !== newObj[field]) {
          const oldVal = this.formatFieldValue(field, oldObj[field])
          const newVal = this.formatFieldValue(field, newObj[field])
          changes.push(`${this.formatFieldName(field)}: ${oldVal} → ${newVal}`)
        }
      })

      return changes
    }
  }
}
